import type { ReactElement } from 'react';
import type { MiddlewareDeliveryStatus } from '../api/contracts';
import { useMonitorApi } from '../app/api-context';
import { useAsyncResource } from '../hooks/use-async-resource';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingBlock } from '../components/Feedback';

/** Como se lee cada estado de una entrega. */
const ESTADOS: Record<MiddlewareDeliveryStatus, string> = {
  planned: 'Planificada',
  posting: 'Enviando a SAP',
  posted: 'Registrada',
  rejected: 'Rechazada por SAP',
  cancelled: 'Anulada antes del POST',
  cancelled_in_sap: 'Anulada en SAP',
  requires_return: 'Va a devolución',
};

/**
 * Entregas SAP del middleware (F25, decisión C): la entrega se crea después de
 * que Janis facturó y descontó el físico. Mientras no se registra, F3 la resta de
 * lo que publica: la tabla de abajo es esa resta, posición por posición.
 */
export function SapDeliveriesPage(): ReactElement {
  const api = useMonitorApi();
  const resource = useAsyncResource(() => api.getSapDeliveries(), [api]);
  const data = resource.data;
  return (
    <div className="page">
      <header className="page-header">
        <h1>Entregas SAP del middleware</h1>
        <p>
          La entrega de mercadería que el middleware crea después de que Janis facturó y descontó el
          físico (F25, decisión C).
        </p>
      </header>
      {resource.error !== null && <ErrorBanner error={resource.error} />}
      {resource.loading && <LoadingBlock />}
      {data && !data.enabled && (
        <section className="panel">
          <div className="panel__body">
            <strong>Apagada (MW_SAP_DELIVERY_ENABLED=false).</strong>
            <p>No se crean entregas nuevas. Lo que ya existía se muestra igual.</p>
          </div>
        </section>
      )}
      {data && (
        <section className="panel" aria-label="Lo que F3 resta">
          <div className="panel__body">
            <h2>Lo que F3 resta por posición</h2>
            {data.pending.length === 0 ? (
              <p>Ninguna entrega pendiente con el descuento de Janis visto.</p>
            ) : (
              <div className="table__wrap">
                <table className="table table--stack">
                  <thead>
                    <tr>
                      <th scope="col">Bodega</th>
                      <th scope="col">SKU</th>
                      <th scope="col">Unidades restadas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pending.map((posicion) => (
                      <tr key={`${posicion.warehouseCode}-${posicion.itemCode}`}>
                        <td className="mono table__lead nowrap">{posicion.warehouseCode}</td>
                        <td className="mono" data-label="SKU">
                          {posicion.itemCode}
                        </td>
                        <td data-label="Unidades restadas">{posicion.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
      {data && (
        <section className="panel" aria-label="Entregas">
          <div className="panel__body">
            <h2>Entregas recientes</h2>
            {data.deliveries.length === 0 ? (
              <p>Todavía no hay entregas del middleware.</p>
            ) : (
              <div className="table__wrap">
                <table className="table table--stack">
                  <thead>
                    <tr>
                      <th scope="col">Pedido de Janis</th>
                      <th scope="col">Estado</th>
                      <th scope="col">Factura de reserva</th>
                      <th scope="col">Entrega en SAP</th>
                      <th scope="col">Líneas</th>
                      <th scope="col">Intentos</th>
                      <th scope="col">Último error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.deliveries.map((entrega) => (
                      <tr key={entrega.id}>
                        <td className="mono table__lead nowrap" title={entrega.key}>
                          {entrega.janisOrderId}
                        </td>
                        <td data-label="Estado">{ESTADOS[entrega.status]}</td>
                        <td className="mono" data-label="Factura de reserva">
                          {entrega.reserveInvoiceDocEntry}
                        </td>
                        <td className="mono" data-label="Entrega en SAP">
                          {entrega.sapDocNum === null
                            ? '—'
                            : `${String(entrega.sapSeries ?? '')}-${String(entrega.sapDocNum)}`}
                        </td>
                        <td data-label="Líneas">
                          {entrega.lines
                            .map((l) => `${l.itemCode} ×${String(l.quantity)}`)
                            .join(', ')}
                        </td>
                        <td data-label="Intentos">{entrega.attempts}</td>
                        <td data-label="Último error">{entrega.lastError ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
